import { useEffect, useState } from "react";
import { DownloadSimple, X } from "@phosphor-icons/react";
import { useLang } from "@/context/LangContext";

/**
 * InstallPrompt — small, dismissable pill at the bottom-right that:
 *  - listens for beforeinstallprompt (Chrome/Edge desktop + Android)
 *  - shows a subtle "Install app" button
 *  - remembers dismissal in localStorage for 7 days
 *  - iOS gets a one-line hint (Add to Home Screen) since Safari doesn't fire the event
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iOS, setIOS] = useState(false);
  const { lang } = useLang();

  useEffect(() => {
    const dismissedAt = Number(localStorage.getItem("anv_install_dismissed") || 0);
    if (Date.now() - dismissedAt < 7 * 24 * 3600 * 1000) return;
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return; // already installed

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari heuristic
    const ua = window.navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const inStandalone = window.navigator.standalone === true;
    if (isIOS && !inStandalone) {
      setIOS(true);
      setVisible(true);
    }

    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const dismiss = () => {
    localStorage.setItem("anv_install_dismissed", String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch {}
    setDeferred(null);
    setVisible(false);
  };

  if (!visible) return null;

  const strings = {
    en: { title: "Install Anveshan", sub: "Get the app on your home screen for one-tap access.", install: "Install", ios: "Tap Share ⇪, then \"Add to Home Screen\"" },
    hi: { title: "अन्वेषण इंस्टॉल करें", sub: "एक-टैप में खोलने के लिए ऐप को होम-स्क्रीन पर लगाएँ।", install: "इंस्टॉल करें", ios: "Share ⇪ पर टैप करें, फिर \"Add to Home Screen\"" },
  };
  const s = strings[lang] || strings.en;

  return (
    <div
      data-testid="install-prompt"
      className="fixed z-40 bottom-4 right-4 max-w-xs samaaj-card p-4 bg-[#F9F6F0] border border-[#E5D9CC] shadow-xl"
      style={{ animation: "fadeUp 380ms ease-out both" }}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "#D95A41" }}>
          <DownloadSimple size={18} color="#F9F6F0" weight="bold" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading text-lg leading-tight">{s.title}</div>
          <div className="text-xs text-[#5C564D] mt-1 leading-relaxed">{iOS ? s.ios : s.sub}</div>
          {!iOS && (
            <button data-testid="btn-install" onClick={install} className="samaaj-btn-primary text-xs mt-3">{s.install}</button>
          )}
        </div>
        <button data-testid="btn-install-dismiss" onClick={dismiss} className="p-1 rounded-full hover:bg-[#F2EBE5]" aria-label="Dismiss">
          <X size={14} className="text-[#5C564D]" />
        </button>
      </div>
    </div>
  );
}
